import React, { memo, useCallback, useDeferredValue, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { FiAlertCircle, FiCreditCard, FiDownload, FiEdit2, FiPlus, FiSearch, FiTrash2 } from 'react-icons/fi';
import { API_BASE_URL } from '../../lib/session';
import { useSessionUser } from '../../lib/permissions';
import { formatDate } from '../../lib/format';
import { usePatientDoctorContext } from '../PatientDoctorContext';
import { BUTTON, CARD, EmptyState, Field, INPUT, Modal, Notice, PageHeader, Pill, Segmented, Select, SIZE, SkeletonRows, TextInput } from '../ui/kit';
import { initials } from '../patients/patientModel';
import PatientPicker from '../patients/PatientPicker';
import { GROUPS, MODES, exportPayments, formatAmount, groupOf, modeLabel, patientOf, paymentAccess, searchText, toDateInput, today } from './paymentModel';

const BASE = `${API_BASE_URL}/api/payments`;
const PAGE_SIZE = 30;
const ICON_BUTTON =
  'flex h-9 w-9 flex-none cursor-pointer items-center justify-center rounded-full border-0 bg-[transparent] text-black/40 transition hover:bg-black/[0.05] hover:text-ink focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-600/40';

const emptyForm = () => ({ patientId: '', ordre: '', modePaiement: '', Donnateur: '', montant: '', datePaiement: today() });
const errorOf = (error, fallback) => (error.response && error.response.data && error.response.data.message) || fallback;

const PaymentRow = memo(({ row, editable, deletable, onEdit, onDelete }) => {
  const { payment, patient } = row;
  const name = patient.nom || 'Patient non précisé';
  return (
    <li className="flex items-center gap-[14px] border-0 border-b-[1px] border-solid border-black/[0.05] px-[16px] py-[14px] last:border-b-0 sm:px-6">
      <span className="hidden h-10 w-10 flex-none items-center justify-center rounded-full bg-lime-50 text-[13px] font-semibold text-lime-700 sm:flex" aria-hidden="true">
        {initials(name)}
      </span>
      <div className="min-w-0 flex-1">
        {row.known ? (
          <Link to={`/patients/${payment.patientId}`} className="block truncate text-[15px] font-semibold text-ink no-underline hover:text-brand-600">
            {name}
          </Link>
        ) : (
          <span className="block truncate text-[15px] font-semibold text-ink">{name}</span>
        )}
        <p className="mb-0 mt-0.5 truncate text-[13px] text-black/45">
          {[row.dateLabel || 'Date non précisée', payment.ordre && `Ordre ${payment.ordre}`, payment.service].filter(Boolean).join(' · ')}
        </p>
        <p className="mb-0 mt-1 flex flex-wrap items-center gap-1.5 text-[12.5px] text-black/50">
          <Pill tone={payment.modePaiement === 'Dons' ? 'violet' : 'neutral'}>{modeLabel(payment.modePaiement)}</Pill>
          {payment.modePaiement === 'Dons' && payment.Donnateur && <span className="truncate">de {payment.Donnateur}</span>}
        </p>
      </div>
      <span className="flex-none text-right text-[15px] font-semibold tabular-nums text-ink">{formatAmount(payment.montant, payment.modePaiement)}</span>
      {(editable || deletable) && (
        <div className="flex flex-none items-center">
          {editable && (
            <button type="button" onClick={() => onEdit(payment)} className={ICON_BUTTON} title="Modifier" aria-label={`Modifier le paiement de ${name}`}>
              <FiEdit2 size={16} aria-hidden="true" />
            </button>
          )}
          {deletable && (
            <button type="button" onClick={() => onDelete(payment)} className={`${ICON_BUTTON} hover:text-red-600`} title="Supprimer" aria-label={`Supprimer le paiement de ${name}`}>
              <FiTrash2 size={16} aria-hidden="true" />
            </button>
          )}
        </div>
      )}
    </li>
  );
});

const PaymentsPage = () => {
  const user = useSessionUser();
  const access = paymentAccess(user);
  const { patients, ensurePatients } = usePatientDoctorContext();
  const [state, setState] = useState({ status: 'loading', payments: [] });
  const [search, setSearch] = useState('');
  const [group, setGroup] = useState('');
  const [unit, setUnit] = useState('');
  const [limit, setLimit] = useState(PAGE_SIZE);
  const [dialog, setDialog] = useState(null); // { mode: 'form', item? } | { mode: 'delete', item }
  const [form, setForm] = useState(emptyForm);
  const [busy, setBusy] = useState(false);
  const [dialogError, setDialogError] = useState('');
  const [notice, setNotice] = useState('');

  const load = useCallback(
    () =>
      axios
        .get(BASE)
        .then((response) => setState({ status: 'ready', payments: Array.isArray(response.data) ? response.data : [] }))
        .catch(() => setState((current) => ({ ...current, status: current.payments.length ? 'ready' : 'error' }))),
    []
  );
  useEffect(() => {
    ensurePatients().catch(() => {});
    load();
  }, [ensurePatients, load]);

  const patientsById = useMemo(() => Object.fromEntries(patients.map((patient) => [patient._id, patient])), [patients]);
  const rows = useMemo(
    () =>
      state.payments
        .map((payment) => {
          const patient = patientOf(payment, patientsById);
          return {
            payment,
            patient,
            known: Boolean(patientsById[payment.patientId]),
            group: groupOf(payment.modePaiement),
            time: new Date(payment.datePaiement || payment.createdAt || 0).getTime() || 0,
            dateLabel: formatDate(payment.datePaiement),
            search: searchText(payment, patient),
          };
        })
        .sort((a, b) => b.time - a.time),
    [state.payments, patientsById]
  );
  const unitsInData = useMemo(() => [...new Set(rows.map((row) => row.payment.service).filter(Boolean))].sort(), [rows]);

  const query = useDeferredValue(search.trim().toLowerCase());
  const shown = useMemo(() => {
    const words = query ? query.split(/\s+/) : [];
    return rows.filter((row) => (!group || row.group === group) && (!unit || row.payment.service === unit) && words.every((word) => row.search.includes(word)));
  }, [rows, group, unit, query]);
  useEffect(() => setLimit(PAGE_SIZE), [group, unit, query]);

  // Totals of the payments shown, per mode (the currencies are not mixed).
  const totals = useMemo(() => {
    const byMode = new Map();
    shown.forEach(({ payment }) => {
      const current = byMode.get(payment.modePaiement) || { count: 0, sum: 0 };
      byMode.set(payment.modePaiement, { count: current.count + 1, sum: current.sum + (Number(payment.montant) || 0) });
    });
    return [...byMode.entries()].sort((a, b) => b[1].count - a[1].count);
  }, [shown]);

  const set = (key, value) => setForm((current) => ({ ...current, [key]: value }));
  const openAdd = () => {
    setForm(emptyForm());
    setDialogError('');
    setNotice('');
    setDialog({ mode: 'form' });
  };
  const openEdit = useCallback((payment) => {
    setForm({
      patientId: payment.patientId || '',
      ordre: payment.ordre || '',
      modePaiement: payment.modePaiement || '',
      Donnateur: payment.Donnateur || '',
      montant: payment.montant === undefined || payment.montant === null ? '' : String(payment.montant),
      datePaiement: toDateInput(payment.datePaiement) || today(),
    });
    setDialogError('');
    setNotice('');
    setDialog({ mode: 'form', item: payment });
  }, []);
  const openDelete = useCallback((payment) => {
    setDialogError('');
    setNotice('');
    setDialog({ mode: 'delete', item: payment });
  }, []);
  const closeDialog = () => !busy && setDialog(null);
  const editing = dialog && dialog.mode === 'form' ? dialog.item : null;

  // As before: validated files of the account's units.
  const pickable = useCallback((patient) => patient.validation === 'Validé' && access.ownsPatient(patient), [access]);
  const modeOptions = useMemo(() => {
    const options = MODES.map(({ value, label }) => ({ value, label }));
    return form.modePaiement && !MODES.some((mode) => mode.value === form.modePaiement) ? [{ value: form.modePaiement, label: form.modePaiement }, ...options] : options;
  }, [form.modePaiement]);

  const save = async () => {
    if (!form.patientId) return setDialogError('Choisissez le dossier du patient.');
    if (!form.modePaiement) return setDialogError('Choisissez le mode de paiement.');
    const montant = Number(String(form.montant).replace(/\s/g, '').replace(',', '.'));
    if (!Number.isFinite(montant) || montant <= 0) return setDialogError('Indiquez un montant supérieur à zéro.');
    if (!form.datePaiement) return setDialogError('Indiquez la date du paiement.');
    setBusy(true);
    setDialogError('');
    const body = { ...form, montant, Donnateur: form.modePaiement === 'Dons' ? form.Donnateur.trim() : '' };
    try {
      if (editing) await axios.put(`${BASE}/${editing._id}`, body);
      else await axios.post(BASE, body);
      // The list is up to date when the dialog closes.
      await load();
      setDialog(null);
      setNotice(editing ? 'Paiement modifié.' : 'Paiement enregistré.');
    } catch (error) {
      setDialogError(errorOf(error, 'Le paiement n’a pas pu être enregistré. Vérifiez la connexion, puis réessayez.'));
    } finally {
      setBusy(false);
    }
    return undefined;
  };

  const remove = async () => {
    setBusy(true);
    setDialogError('');
    try {
      await axios.delete(`${BASE}/${dialog.item._id}`);
      await load();
      setDialog(null);
      setNotice('Paiement supprimé.');
    } catch (error) {
      setDialogError(errorOf(error, 'Le paiement n’a pas pu être supprimé. Réessayez.'));
    } finally {
      setBusy(false);
    }
  };

  const chip = (value, label) => (
    <button
      key={value || 'all'}
      type="button"
      aria-pressed={group === value}
      onClick={() => setGroup(value)}
      className={`inline-flex h-9 flex-none cursor-pointer items-center whitespace-nowrap rounded-full border-0 px-[14px] text-[13.5px] font-medium focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-600/40 ${
        group === value ? 'bg-ink text-white' : 'bg-white text-black/65 shadow-[0_1px_2px_rgba(0,0,0,0.06)] hover:text-ink'
      }`}
    >
      {label}
    </button>
  );
  const plural = (count) => `${count} paiement${count > 1 ? 's' : ''}`;

  return (
    <main className="mx-auto max-w-6xl px-[20px] pb-16 pt-8 sm:px-8 lg:px-10 lg:pt-10">
      <PageHeader
        eyebrow="Espace protégé"
        title="Paiements"
        subtitle={state.status === 'ready' ? `${plural(rows.length)} · ${access.isAdmin ? 'toutes les unités' : `unité ${user.service || 'non précisée'}`}` : 'Chargement des paiements…'}
        actions={
          <>
            {access.canExport && (
              <button type="button" onClick={() => exportPayments(shown)} disabled={!shown.length} className={`${BUTTON.secondary} ${SIZE.md}`}>
                <FiDownload size={15} aria-hidden="true" />
                Excel
              </button>
            )}
            {access.canAdd && (
              <button type="button" onClick={openAdd} className={`${BUTTON.primary} ${SIZE.md}`}>
                <FiPlus size={16} aria-hidden="true" />
                Nouveau paiement
              </button>
            )}
          </>
        }
      />

      <div className="mt-8 flex flex-col gap-[12px]">
        {notice && <Notice tone="green">{notice}</Notice>}
        <div className="relative">
          <FiSearch size={17} aria-hidden="true" className="pointer-events-none absolute left-[14px] top-1/2 -translate-y-1/2 text-black/35" />
          <input
            type="search"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Patient, n° d’ordre, donateur, montant, date…"
            aria-label="Rechercher un paiement"
            className={`${INPUT} h-11 pl-[42px]`}
          />
        </div>
        <div className="flex flex-col gap-[12px] lg:flex-row lg:items-center lg:justify-between">
          <div className="-mx-[20px] flex gap-2 overflow-x-auto px-[20px] pb-1 [scrollbar-width:none] sm:mx-0 sm:flex-wrap sm:px-0 [&::-webkit-scrollbar]:hidden">
            {chip('', 'Tous')}
            {GROUPS.map((name) => chip(name, name))}
          </div>
          {unitsInData.length > 1 && (
            <Segmented value={unit} onChange={setUnit} allowEmpty={false} options={[{ value: '', label: 'Toutes les unités' }, ...unitsInData.map((name) => ({ value: name, label: name }))]} />
          )}
        </div>
        {totals.length > 0 && (
          <ul
            className="-mx-[20px] mb-0 mt-0 flex list-none gap-2 overflow-x-auto px-[20px] pb-1 [scrollbar-width:none] sm:mx-0 sm:flex-wrap sm:px-0 [&::-webkit-scrollbar]:hidden"
            aria-label="Totaux des paiements affichés"
          >
            {totals.map(([mode, total]) => (
              <li key={mode || 'none'} className="flex-none rounded-2xl bg-white px-[14px] py-[10px] shadow-[0_1px_2px_rgba(0,0,0,0.06)]">
                <span className="block text-[12px] font-medium text-black/45">
                  {modeLabel(mode)} · {plural(total.count)}
                </span>
                <span className="block text-[15px] font-semibold tabular-nums text-ink">{formatAmount(total.sum, mode)}</span>
              </li>
            ))}
          </ul>
        )}
      </div>

      <section className={`${CARD} mt-[16px] overflow-hidden`}>
        {state.status === 'loading' ? (
          <div className="p-6">
            <SkeletonRows rows={6} />
          </div>
        ) : state.status === 'error' ? (
          <EmptyState icon={FiAlertCircle} title="Paiements indisponibles">
            Ils n’ont pas pu être chargés. Vérifiez la connexion, puis rechargez la page.
          </EmptyState>
        ) : shown.length === 0 ? (
          <EmptyState icon={FiCreditCard} title="Aucun paiement">
            {rows.length ? 'Aucun paiement ne correspond à ces filtres.' : 'Les paiements de votre unité apparaîtront ici.'}
          </EmptyState>
        ) : (
          <>
            <ul className="m-0 list-none p-0">
              {shown.slice(0, limit).map((row) => (
                <PaymentRow key={row.payment._id} row={row} editable={access.canEdit(row.payment)} deletable={access.canDelete(row.payment)} onEdit={openEdit} onDelete={openDelete} />
              ))}
            </ul>
            {limit < shown.length && (
              <div className="flex flex-col items-center gap-1 border-0 border-t-[1px] border-solid border-black/[0.05] p-[16px] text-center">
                <button type="button" onClick={() => setLimit((count) => count + PAGE_SIZE)} className={`${BUTTON.ghost} ${SIZE.md}`}>
                  Afficher {Math.min(PAGE_SIZE, shown.length - limit)} paiements de plus
                </button>
                <span className="text-[12.5px] text-black/40">
                  {limit} sur {shown.length} affichés
                </span>
              </div>
            )}
          </>
        )}
      </section>

      <Modal
        open={Boolean(dialog && dialog.mode === 'form')}
        title={editing ? 'Modifier le paiement' : 'Nouveau paiement'}
        onClose={closeDialog}
        size="lg"
        footer={
          <>
            <button type="button" onClick={closeDialog} className={`${BUTTON.secondary} ${SIZE.md}`}>
              Annuler
            </button>
            <button type="button" onClick={save} disabled={busy} className={`${BUTTON.primary} ${SIZE.md}`}>
              {busy ? 'Enregistrement…' : 'Enregistrer'}
            </button>
          </>
        }
      >
        <div className="flex flex-col gap-[18px]">
          <Field id="payment-patient" label="Dossier patient" required hint="Dossiers validés de votre unité.">
            <PatientPicker
              id="payment-patient"
              single
              filter={pickable}
              values={form.patientId ? [form.patientId] : []}
              onChange={(ids) => {
                set('patientId', ids[0] || '');
                setDialogError('');
              }}
            />
          </Field>
          <div className="grid grid-cols-1 gap-[18px] sm:grid-cols-2">
            <Field id="payment-mode" label="Mode de paiement" required>
              <Select id="payment-mode" value={form.modePaiement} onChange={(e) => set('modePaiement', e.target.value)} options={modeOptions} placeholder="Choisir le mode…" />
            </Field>
            <Field id="payment-amount" label="Montant" required>
              <TextInput id="payment-amount" inputMode="decimal" value={form.montant} onChange={(e) => set('montant', e.target.value)} placeholder="Ex. 150000" />
            </Field>
            {form.modePaiement === 'Dons' && (
              <Field id="payment-donor" label="Donateur">
                <TextInput id="payment-donor" value={form.Donnateur} onChange={(e) => set('Donnateur', e.target.value)} placeholder="Nom du donateur" />
              </Field>
            )}
            <Field id="payment-date" label="Date du paiement" required>
              <TextInput id="payment-date" type="date" value={form.datePaiement} onChange={(e) => set('datePaiement', e.target.value)} />
            </Field>
            <Field id="payment-order" label="N° d’ordre">
              <TextInput id="payment-order" value={form.ordre} onChange={(e) => set('ordre', e.target.value)} placeholder="Numéro de reçu ou d’ordre" />
            </Field>
          </div>
          {dialogError && <Notice tone="red">{dialogError}</Notice>}
        </div>
      </Modal>

      <Modal
        open={Boolean(dialog && dialog.mode === 'delete')}
        title="Supprimer le paiement"
        onClose={closeDialog}
        footer={
          <>
            <button type="button" onClick={closeDialog} className={`${BUTTON.secondary} ${SIZE.md}`}>
              Annuler
            </button>
            <button type="button" onClick={remove} disabled={busy} className={`${BUTTON.dangerSolid} ${SIZE.md}`}>
              {busy ? 'Suppression…' : 'Supprimer'}
            </button>
          </>
        }
      >
        {dialog && dialog.mode === 'delete' && (
          <div className="flex flex-col gap-[12px]">
            <p className="m-0 text-[14.5px] leading-relaxed text-black/55">
              Le paiement de <strong className="text-ink">{formatAmount(dialog.item.montant, dialog.item.modePaiement)}</strong> pour{' '}
              {patientOf(dialog.item, patientsById).nom || 'ce patient'} sera supprimé définitivement. L’opération est notée dans le journal.
            </p>
            {dialogError && <Notice tone="red">{dialogError}</Notice>}
          </div>
        )}
      </Modal>
    </main>
  );
};

export default PaymentsPage;
